/**
 * Walk rule graphs × attribute options to discover which scene targets / materials
 * can appear for a product. Used to prefetch GLBs and warm material graphs so
 * visibility / apply-material swaps snap instead of loading on first click.
 */

import type { ProductModel } from '@config3d/contracts';
import {
  evaluateRuleGraphsForSelections,
  type RuleAction,
  type SerializedGraph,
} from './client-rule-eval';

const MAX_COMBOS = 96;

function attributeOptions(productModel: ProductModel | null, key: string): string[] {
  const attr = productModel?.attributes?.find((a) => a.key === key);
  if (!attr) return [];
  const opts = (attr.values || []).map((o: { key?: string; id?: string }) =>
    String(o?.key ?? o?.id ?? ''),
  );
  return opts.filter((k) => k.length > 0);
}

/** Attribute keys referenced by any rule node (drives which dims we vary). */
export function listRuleAttributeKeys(graphs: SerializedGraph[]): string[] {
  const keys = new Set<string>();
  for (const graph of graphs) {
    for (const node of graph.nodes ?? []) {
      if (node.type !== 'attribute' && node.type !== 'switch') continue;
      const k = node.data?.attributeKey;
      if (typeof k === 'string' && k.length > 0) keys.add(k);
    }
  }
  return [...keys].sort();
}

function applyVisibilityActions(
  actions: RuleAction[],
  hidden: Set<string>,
  shown: Set<string>,
  hiddenTags: Set<string>,
  shownTags: Set<string>,
  materialIds: Set<string>,
) {
  const parseVisibilityRefs = (raw: unknown): Array<{ kind: 'scene' | 'tag'; id: string }> => {
    if (!Array.isArray(raw)) return [];
    const out: Array<{ kind: 'scene' | 'tag'; id: string }> = [];
    for (const x of raw) {
      if (x == null || typeof x !== 'object') continue;
      const k = (x as { kind?: string }).kind;
      const id = (x as { id?: string }).id;
      if ((k === 'scene' || k === 'tag') && typeof id === 'string' && id.length > 0) {
        out.push({ kind: k, id });
      }
    }
    return out;
  };

  for (const action of actions) {
    if (action.type === 'apply-material' && action.value != null) {
      materialIds.add(String(action.value));
      continue;
    }
    if (action.type === 'set-visibility' && action.value != null && typeof action.value === 'object') {
      const v = action.value as { show?: unknown; hide?: unknown };
      for (const t of parseVisibilityRefs(v.hide)) {
        if (t.kind === 'tag') hiddenTags.add(t.id);
        else hidden.add(t.id);
      }
      for (const t of parseVisibilityRefs(v.show)) {
        if (t.kind === 'tag') shownTags.add(t.id);
        else shown.add(t.id);
      }
      continue;
    }
    if (action.type === 'hide-object' || action.type === 'hide-component') hidden.add(action.targetId);
    if (action.type === 'show-object' || action.type === 'show-component') shown.add(action.targetId);
    if (action.type === 'hide-tagged') hiddenTags.add(action.targetId);
    if (action.type === 'show-tagged') shownTags.add(action.targetId);
  }
}

export interface RuleCompPrefetchPlan {
  /** Scene object ids / names referenced by any visibility action across option combos. */
  sceneTargets: Set<string>;
  /** Tag ids referenced by visibility actions. */
  tagTargets: Set<string>;
  /** Material ids/names from apply-material across option combos. */
  materialIds: Set<string>;
  combosEvaluated: number;
}

/**
 * Evaluate a capped set of selection combos (vary one wired attribute at a time from base,
 * plus cartesian of small option sets) to discover reachable comps.
 */
export function buildRuleCompPrefetchPlan(
  graphs: SerializedGraph[],
  productModel: ProductModel | null,
  baseSelections: Record<string, unknown>,
): RuleCompPrefetchPlan {
  const sceneTargets = new Set<string>();
  const tagTargets = new Set<string>();
  const materialIds = new Set<string>();
  let combosEvaluated = 0;

  if (graphs.length === 0) {
    return { sceneTargets, tagTargets, materialIds, combosEvaluated };
  }

  const collect = (selections: Record<string, unknown>) => {
    if (combosEvaluated >= MAX_COMBOS) return;
    combosEvaluated++;
    const actions = evaluateRuleGraphsForSelections(graphs, selections, productModel) ?? [];
    const hidden = new Set<string>();
    const shown = new Set<string>();
    const hiddenTags = new Set<string>();
    const shownTags = new Set<string>();
    applyVisibilityActions(actions, hidden, shown, hiddenTags, shownTags, materialIds);
    for (const id of hidden) sceneTargets.add(id);
    for (const id of shown) sceneTargets.add(id);
    for (const t of hiddenTags) tagTargets.add(t);
    for (const t of shownTags) tagTargets.add(t);
  };

  collect(baseSelections);

  const keys = listRuleAttributeKeys(graphs);
  // Single-axis sweeps from base — catches most exclusivity hide/show graphs.
  for (const key of keys) {
    const opts = attributeOptions(productModel, key);
    for (const opt of opts) {
      if (combosEvaluated >= MAX_COMBOS) break;
      if (String(baseSelections[key] ?? '') === opt) continue;
      collect({ ...baseSelections, [key]: opt });
    }
  }

  // Small cartesian when ≤2 wired attrs with small catalogs.
  if (keys.length === 2 && combosEvaluated < MAX_COMBOS) {
    const [k0, k1] = keys;
    const o0 = attributeOptions(productModel, k0!);
    const o1 = attributeOptions(productModel, k1!);
    if (o0.length * o1.length <= 48) {
      for (const a of o0) {
        for (const b of o1) {
          if (combosEvaluated >= MAX_COMBOS) break;
          collect({ ...baseSelections, [k0!]: a, [k1!]: b });
        }
      }
    }
  }

  return { sceneTargets, tagTargets, materialIds, combosEvaluated };
}
